import { useIsMobile } from '@/hooks/use-is-mobile';
import clsx from 'clsx';
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';

interface Props {
  activeIndex: number;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  imageUrls: string[];
  generateAlt: (index: number) => string;
}

const SLIDE_WIDTH = 800;
const SLIDE_GAP = 10;
const SLIDE_STEP = SLIDE_WIDTH + SLIDE_GAP;

const ImageCarouselPreview = (props: Props) => {
  const { activeIndex, open, setOpen, imageUrls, generateAlt } = props;
  const { isMobile } = useIsMobile();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const scrollToIndex = useCallback(
    (index: number, smooth = true) => {
      const container = containerRef.current;
      if (!container) return;
      const target = index * SLIDE_STEP;
      container.scrollTo({ left: target, behavior: smooth ? 'smooth' : 'instant' });
      setSelectedIndex(index);
    },
    [],
  );

  useEffect(() => {
    if (!open) return;
    setTimeout(() => scrollToIndex(activeIndex, false), 50);
  }, [activeIndex, open, scrollToIndex]);

  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    if (isMobile) {
      const scrollTop = container.scrollTop;
      const containerHeight = container.clientHeight;
      const index = Math.round(scrollTop / containerHeight);
      setSelectedIndex(Math.min(index, imageUrls.length - 1));
    } else {
      const scrollLeft = container.scrollLeft;
      const index = Math.round(scrollLeft / SLIDE_STEP);
      setSelectedIndex(Math.min(index, imageUrls.length - 1));
    }
  }, [isMobile, imageUrls.length]);

  const goPrev = () => {
    const container = containerRef.current;
    if (!container) return;
    container.scrollBy({ left: -SLIDE_STEP, behavior: 'smooth' });
  };

  const goNext = () => {
    const container = containerRef.current;
    if (!container) return;
    container.scrollBy({ left: SLIDE_STEP, behavior: 'smooth' });
  };

  const canScrollPrev = selectedIndex > 0;
  const canScrollNext = selectedIndex < imageUrls.length - 1;

  return (
    <Dialog
      open={open}
      onOpenChange={setOpen}
    >
      <DialogContent
        className='block h-screen w-screen p-0 supports-[height:100dvh]_h-[100dvh] lg_max-h-[999px] lg_max-w-none'
        aria-describedby={undefined}
        showCloseButton={false}
      >
        <DialogTitle className='hidden'>image preview</DialogTitle>
        <div className='relative h-full lg_w-full'>
          {!isMobile && (canScrollPrev || canScrollNext) ? (
            <button
              type='button'
              className={clsx(
                'hidden h-10 w-10 items-center justify-center rounded-[50%] bg-black lg_flex lg_hover_bg-4c4c4c',
                'absolute left-10 top-1/2 z-[2] -translate-y-1/2',
              )}
              onClick={goPrev}
            >
              <svg xmlns='http://www.w3.org/2000/svg' width='10' height='16' fill='none' viewBox='0 0 10 16'>
                <path
                  fill='#fff'
                  d='M7.793 15.686c.21.203.49.314.784.314.297 0 .574-.111.784-.314s.327-.476.327-.764c0-.29-.115-.56-.327-.766L2.994 7.998l6.37-6.157c.21-.203.328-.476.328-.764 0-.29-.115-.56-.328-.763A1.12 1.12 0 0 0 8.58 0c-.297 0-.574.111-.783.314L.637 7.235a1.055 1.055 0 0 0 0 1.53z'
                />
              </svg>
            </button>
          ) : null}
          {!isMobile && (canScrollPrev || canScrollNext) ? (
            <button
              type='button'
              className={clsx(
                'hidden h-10 w-10 items-center justify-center rounded-[50%] bg-black lg_flex lg_hover_bg-4c4c4c',
                'absolute right-10 top-1/2 z-[2] -translate-y-1/2',
              )}
              onClick={goNext}
            >
              <svg xmlns='http://www.w3.org/2000/svg' width='10' height='16' fill='none' viewBox='0 0 10 16'>
                <path
                  fill='#fff'
                  d='M2.207 15.686c-.21.203-.49.314-.784.314-.297 0-.574-.111-.784-.314a1.055 1.055 0 0 1 0-1.53l6.367-6.158-6.37-6.157a1.06 1.06 0 0 1-.328-.764c0-.29.115-.56.328-.763C.846.111 1.122 0 1.42 0c.297 0 .574.111.783.314l7.16 6.921a1.055 1.055 0 0 1 0 1.53z'
                />
              </svg>
            </button>
          ) : null}
          <div
            className={clsx(
              'absolute right-5 top-5 z-[2] rounded-[63px] bg-black lg_left-1/2 lg_right-auto lg_top-10 lg_-translate-x-1/2',
              'px-5 py-[7px] font-roboto-regular text-[13px] leading-4',
            )}
          >
            <span className='text-white'>{selectedIndex + 1}</span>
            <span className='text-white'>/{imageUrls.length}</span>
          </div>
          <button
            type='button'
            className={clsx(
              'absolute bottom-5 left-1/2 z-[2] flex h-10 w-10 -translate-x-1/2 items-center justify-center rounded-[50%] bg-black lg_bottom-10 lg_h-[60px] lg_w-[60px] lg_hover_bg-4c4c4c',
            )}
            onClick={() => setOpen(false)}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='15'
              height='16'
              fill='none'
              viewBox='0 0 15 16'
              className='h-auto lg_w-[22px]'
            >
              <path
                fill='#fff'
                d='M13.454 15.354 7.5 9.401l-5.954 5.953a.497.497 0 0 1-.7 0l-.701-.7a.497.497 0 0 1 0-.7L6.1 8 .145 2.046a.497.497 0 0 1 0-.7l.7-.701a.497.497 0 0 1 .701 0L7.5 6.599 13.454.645a.497.497 0 0 1 .7 0l.702.7a.496.496 0 0 1 0 .701L8.9 8l5.954 5.954a.496.496 0 0 1 0 .7l-.701.701a.494.494 0 0 1-.7 0'
              />
            </svg>
          </button>
          <div
            ref={containerRef}
            onScroll={handleScroll}
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            className={clsx(
              'h-full [&::-webkit-scrollbar]:hidden',
              isMobile
                ? 'snap-y snap-mandatory overflow-x-hidden overflow-y-auto'
                : 'flex overflow-x-auto overflow-y-hidden',
            )}
          >
            {imageUrls.map((x, i) => {
              const alt = generateAlt(i);
              return (
                <div
                  key={x}
                  className={clsx(
                    'flex-shrink-0',
                    isMobile
                      ? 'w-full snap-start'
                      : 'w-[800px] pl-2.5',
                  )}
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={x}
                    alt={alt}
                    title={alt}
                    className='h-auto min-h-0 w-full'
                  />
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageCarouselPreview;
